/**
 * MagicBI Design System - StatsCard Component
 * 
 * Specialized card component for displaying statistics
 * with values, labels, and optional trends.
 */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Card, CardBody } from '../Card/Card';
import { Display, Text } from '../../primitives/Typography/Typography';
import { Flex } from '../../primitives/Layout/Layout';

// StatsCard props interface
export interface StatsCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Statistic value */
  value: string | number;
  /** Statistic label */
  label: string;
  /** Optional trend indicator */
  trend?: {
    value: string | number;
    direction: 'up' | 'down' | 'neutral';
    label?: string;
  };
  /** Optional icon */
  icon?: React.ReactNode;
  /** Value color */
  valueColor?: string;
  /** Card variant */
  variant?: 'default' | 'elevated' | 'outlined';
  /** Card size */
  size?: 'sm' | 'md' | 'lg';
  /** Whether card is interactive */
  interactive?: boolean;
}

// Stat value
const StatValue = styled(Display)<{ $color?: string }>`
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing.semantic.inset.xs};
  
  ${({ $color, theme }) => $color && css`
    color: ${$color.startsWith('#') ? $color : (theme.colors as any)[$color] || $color};
  `}
`;

// Stat label
const StatLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.875rem;
  margin: 0;
`;

// Trend indicator
const TrendIndicator = styled.div<{ $direction: 'up' | 'down' | 'neutral' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: ${({ theme }) => theme.spacing.semantic.inset.xs};
  
  ${({ $direction, theme }) => {
    switch ($direction) {
      case 'up':
        return css`
          color: ${theme.colors.success[600]};
        `;
      case 'down':
        return css`
          color: ${theme.colors.error[600]};
        `;
      case 'neutral':
      default:
        return css`
          color: ${theme.colors.text.secondary};
        `;
    }
  }}
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

// Icon container
const IconContainer = styled.div`
  color: ${({ theme }) => theme.colors.interactive.primary};
  margin-bottom: ${({ theme }) => theme.spacing.semantic.inset.sm};
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

// Styled stats card
const StyledStatsCard = styled(Card)<{ $interactive?: boolean }>`
  text-align: center;
  transition: ${({ theme }) => theme.transitions.semantic.interactive.button.all};
  
  ${({ $interactive }) => $interactive && css`
    cursor: pointer;
    
    &:hover {
      transform: translateY(-2px);
    }
  `}
`;

// Trend arrow icons
const TrendUpIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 14l5-5 5 5z"/>
  </svg>
);

const TrendDownIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 10l5 5 5-5z"/>
  </svg>
);

const TrendNeutralIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 12l6 0"/>
  </svg>
);

// StatsCard component
export const StatsCard = forwardRef<HTMLDivElement, StatsCardProps>(
  (
    {
      value,
      label,
      trend,
      icon,
      valueColor,
      variant = 'default',
      size = 'md',
      interactive = false,
      ...props
    },
    ref
  ) => {
    const getTrendIcon = (direction: 'up' | 'down' | 'neutral') => {
      switch (direction) {
        case 'up':
          return <TrendUpIcon />;
        case 'down':
          return <TrendDownIcon />;
        case 'neutral':
        default:
          return <TrendNeutralIcon />;
      }
    };

    return (
      <StyledStatsCard
        ref={ref}
        variant={variant}
        size={size}
        $interactive={interactive}
        interactive={interactive}
        {...props}
      >
        <CardBody>
          {icon && (
            <IconContainer>
              {icon}
            </IconContainer>
          )}
          
          <StatValue 
            $color={valueColor}
          >
            {value}
          </StatValue>
          
          <StatLabel>
            {label}
          </StatLabel>
          
          {trend && (
            <TrendIndicator $direction={trend.direction}>
              {getTrendIcon(trend.direction)}
              <span>{trend.value}</span>
              {trend.label && <span>{trend.label}</span>}
            </TrendIndicator>
          )}
        </CardBody>
      </StyledStatsCard>
    );
  }
);

StatsCard.displayName = 'StatsCard';

export default StatsCard;
